import { React, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { api } from "../../utils/api";
import CustomerSubscriptionCard from "./CustomerSubscriptionCard";
import { Tabs, Tab, Box } from "@mui/material";
import ContractAcceptanceModal from "../../components/ContractAcceptanceModal";
import {
  subscriptionRequiresContract,
  isLinkedAddOnSubscription,
} from "../../utils/subscriptionContractGate";
import { isHiddenFromCustomerProduct } from "../../utils/hiddenFromCustomerProduct";

const MySubscriptions = () => {
  const location = useLocation();
  const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [inactiveSubscriptions, setInactiveSubscriptions] = useState([]);
  const [loading, setLoading] = useState([true]);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedProduct, setExpandedProduct] = useState(null);

  // Contract acceptance modal state
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [pendingContractsQueue, setPendingContractsQueue] = useState([]);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      // Fetch active subscriptions
      const response = await api(`v1/subscription/customer/${currentUser._id}`, "GET");
      if (response.data.success) {
        const allSubscriptions = response.data.data.allSubscriptions.filter(
          (element) => element !== null && !isHiddenFromCustomerProduct(element.product)
        );

        // Separate subscriptions into active and inactive based on validFromDateUTC and validUntilDateUTC
        const now = new Date();
        const activeSubscriptions = [];
        const inactiveSubscriptions = [];

        // Organize subscriptions into main and add-ons
        const mainSubscriptionsMap = new Map();

        allSubscriptions.forEach((subscription) => {
          // Check if subscription has expired (validUntilDateUTC is in the past)
          const isExpired = subscription.validUntilDateUTC && new Date(subscription.validUntilDateUTC) < now;

          // Check if subscription has a future validFromDateUTC
          const isFuture = subscription.validFromDateUTC && new Date(subscription.validFromDateUTC) > now;

          // Check if this is an add-on subscription
          const isAddOn = subscription.note && subscription.note.mainSubscriptionId;

          if (isExpired) {
            // Subscription has expired, don't show it at all
            console.log(
              `Subscription ${subscription.product.name} has expired (validUntilDateUTC: ${subscription.validUntilDateUTC}), excluding from display`
            );
            // Don't add to either list - exclude expired subscriptions
          } else if (isFuture) {
            // Subscription has a future validFromDateUTC, should be inactive
            console.log(
              `Subscription ${subscription.product.name} has future validFromDateUTC: ${subscription.validFromDateUTC}, adding to inactive`
            );
            inactiveSubscriptions.push(subscription);
          } else {
            // Subscription is currently valid (not expired and not future), should be active
            console.log(`Subscription ${subscription.product.name} is currently valid, adding to active`);

            if (isAddOn) {
              // This is an add-on, group it with its main subscription
              const mainSubId = subscription.note.mainSubscriptionId;
              const mainSubIdStr = typeof mainSubId === "string" ? mainSubId : mainSubId?.toString();

              // Try to find existing group by ID (string or ObjectId)
              let group = null;
              for (const [key, value] of mainSubscriptionsMap.entries()) {
                const keyStr = typeof key === "string" ? key : key?.toString();
                if (keyStr === mainSubIdStr) {
                  group = value;
                  break;
                }
              }

              if (!group) {
                // Find the main subscription
                const mainSub = allSubscriptions.find((sub) => {
                  const subIdStr = sub._id?.toString();
                  return subIdStr === mainSubIdStr;
                });
                if (mainSub) {
                  group = {
                    main: mainSub,
                    addOns: []
                  };
                  mainSubscriptionsMap.set(mainSub._id, group);
                } else {
                  // Main subscription not found, treat as standalone
                  activeSubscriptions.push(subscription);
                }
              }

              if (group) {
                group.addOns.push(subscription);
              }
            } else {
              // This is a main subscription
              if (!mainSubscriptionsMap.has(subscription._id)) {
                mainSubscriptionsMap.set(subscription._id, {
                  main: subscription,
                  addOns: []
                });
              }
            }
          }
        });

        // Convert map to array and add to activeSubscriptions
        mainSubscriptionsMap.forEach((group) => {
          activeSubscriptions.push(group);
        });

        console.log(
          `Active subscriptions: ${activeSubscriptions.length}, Inactive subscriptions: ${inactiveSubscriptions.length}`
        );
        setMySubscriptions(activeSubscriptions);
        setInactiveSubscriptions(inactiveSubscriptions);
      }

      // Note: Inactive subscriptions are now handled in the main fetch above
    } catch (error) {
      console.error("API error:", error);
    }
    setLoading(false);
  };

  // Function to aggregate upcoming subscriptions by product
  const aggregateUpcomingSubscriptions = () => {
    const aggregated = {};

    inactiveSubscriptions.forEach((subscription) => {
      const productId = subscription.product._id;

      if (!aggregated[productId]) {
        aggregated[productId] = {
          product: subscription.product,
          subscriptions: [],
          totalCount: 0,
          earliestDate: subscription.validFromDateUTC
        };
      }

      aggregated[productId].subscriptions.push(subscription);
      aggregated[productId].totalCount++;

      // Keep track of the earliest date
      if (new Date(subscription.validFromDateUTC) < new Date(aggregated[productId].earliestDate)) {
        aggregated[productId].earliestDate = subscription.validFromDateUTC;
      }
    });

    return Object.values(aggregated);
  };

  // Check for pending contracts across all customer subscriptions
  const checkForPendingContracts = useCallback(async () => {
    try {
      console.log("🔍 MySubscriptions: Checking for pending contracts, subscription count:", mySubscriptions.length);

      // Array to collect all pending contracts
      const pendingContracts = [];

      // Only check for contracts if customer has subscriptions
      if (mySubscriptions.length > 0) {
        // Check each subscription for pending contracts
        for (const subscription of mySubscriptions) {
          console.log(
            "🔎 MySubscriptions: Checking subscription:",
            subscription._id,
            "Product type:",
            subscription.product?.type
          );

          if (isLinkedAddOnSubscription(subscription)) {
            console.log("⏭️ MySubscriptions: Linked add-on uses main contract — skip duplicate modal");
            continue;
          }

          if (subscriptionRequiresContract(subscription.product, subscription)) {
            console.log("✅ MySubscriptions: Contract-gated product, checking contract requirement...");

            const contractResponse = await api(`v1/contract/check-required/${subscription._id}`, "GET");
            console.log("📄 MySubscriptions: Contract required response:", contractResponse.data);

            if (contractResponse.data.success && contractResponse.data.data.contractAcceptanceRequired) {
              console.log("✅ MySubscriptions: Contract acceptance required, checking for pending contract...");

              // Check if there's a pending contract
              const pendingResponse = await api(`v1/contract/check-pending/${subscription._id}`, "GET");
              console.log("⏳ MySubscriptions: Pending contract response:", pendingResponse.data);

              if (pendingResponse.data.success && pendingResponse.data.data) {
                console.log("🎯 MySubscriptions: Found pending contract for subscription:", subscription._id);
                // Add to pending contracts queue
                pendingContracts.push({
                  contractData: pendingResponse.data.data,
                  subscription: subscription
                });
              } else {
                console.log("❌ MySubscriptions: No pending contract found");
              }
            } else {
              console.log("❌ MySubscriptions: Contract acceptance not required or API error");
            }
          } else {
            console.log("⏭️ MySubscriptions: Skipping product without contract requirement");
          }
        }
      } else {
        console.log("📭 MySubscriptions: No subscriptions found");
      }

      // Store all pending contracts in queue
      if (pendingContracts.length > 0) {
        console.log(`📝 MySubscriptions: Found ${pendingContracts.length} pending contract(s) to sign`);
        setPendingContractsQueue(pendingContracts);
        setContractModalOpen(true);
      } else {
        console.log("✅ MySubscriptions: No pending contracts found");
        setPendingContractsQueue([]);
      }
    } catch (error) {
      console.error("💥 MySubscriptions: Error checking for pending contracts:", error);
    }
  }, [mySubscriptions]);

  // Re-prompt for pending contracts when subscriptions load, route is active, or tab regains focus
  useEffect(() => {
    if (mySubscriptions.length > 0) {
      checkForPendingContracts();
    }
  }, [mySubscriptions, checkForPendingContracts]);

  useEffect(() => {
    if (location.pathname === "/my-subscriptions" && mySubscriptions.length > 0) {
      checkForPendingContracts();
    }
  }, [location.pathname, mySubscriptions.length, checkForPendingContracts]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && mySubscriptions.length > 0) {
        checkForPendingContracts();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [mySubscriptions.length, checkForPendingContracts]);

  const handleContractAccepted = () => {
    console.log("✅ MySubscriptions: All pending contracts signed in session");
    setContractModalOpen(false);
    setPendingContractsQueue([]);
  };

  const handleContractModalClose = () => {
    setContractModalOpen(false);
    setTimeout(() => checkForPendingContracts(), 300);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setExpandedProduct(null); // Reset expanded product when switching tabs
  };

  const handleProductExpand = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Function to get product-specific icon
  const getProductIcon = (productName) => {
    const name = productName.toLowerCase();

    if (name.includes("wash") || name.includes("interior") || name.includes("exterior")) {
      // Car icon for wash services
      return (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
        </svg>
      );
    } else if (name.includes("polish") || name.includes("shine") || name.includes("glow")) {
      // Polish/shine icon
      return (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      );
    } else if (name.includes("detail") || name.includes("leather") || name.includes("wheel")) {
      // Detail service icon
      return (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      );
    } else if (name.includes("headlight") || name.includes("restoration")) {
      // Headlight/restoration icon
      return (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" />
        </svg>
      );
    } else if (name.includes("unlimited")) {
      // Unlimited service icon
      return (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
        </svg>
      );
    } else {
      // Default service icon
      return (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      );
    }
  };

  return (
    <div className="page subscription flex w-full flex-1 flex-col items-center bg-gray-50 p-4 sm:p-6">
      {loading && <GlobalLoadingSpinner />}
      <div className="pageTitle mb-6 sm:mb-8 text-center px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Redeem your Subscription</h2>
        <span className="text-base sm:text-lg text-gray-600">Thank you for registering</span>
      </div>

      {/* Tabbed Interface */}
      <Box sx={{ width: "100%", maxWidth: "1400px" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            marginBottom: 3,
            backgroundColor: "white",
            borderRadius: "12px 12px 0 0",
            padding: "0 12px",
            "& .MuiTab-root": {
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              padding: { xs: "12px 8px", sm: "14px 16px", md: "16px 24px" },
              minHeight: { xs: "48px", sm: "54px", md: "60px" }
            }
          }}
        >
          <Tab
            label={`Active (${mySubscriptions.length})`}
            sx={{
              fontWeight: activeTab === 0 ? "bold" : "normal",
              textTransform: "none"
            }}
          />
          <Tab
            label={`Upcoming (${inactiveSubscriptions.length})`}
            sx={{
              fontWeight: activeTab === 1 ? "bold" : "normal",
              textTransform: "none"
            }}
          />
        </Tabs>

        {/* Active Subscriptions Tab */}
        {activeTab === 0 && (
          <div className="bg-white rounded-b-xl p-4 sm:p-6">
            <div className="flex flex-col gap-6">
              {mySubscriptions.length > 0 ? (
                mySubscriptions.map((subscriptionOrGroup) => {
                  // Check if this is a grouped subscription (has main and addOns) or a single subscription
                  if (subscriptionOrGroup.main) {
                    // This is a grouped subscription
                    const { main, addOns } = subscriptionOrGroup;
                    return (
                      <div key={main._id} className="flex flex-col gap-4">
                        {/* Main Subscription */}
                        <div className="relative">
                          <div className="absolute -left-2 top-0 bottom-0 w-1 bg-blue-500 rounded-l-lg"></div>
                          <CustomerSubscriptionCard
                            key={main._id}
                            subscription={main}
                            isInactive={false}
                            isMainSubscription={true}
                          />
                        </div>
                        {/* Add-on Subscriptions */}
                        {addOns.length > 0 && (
                          <div className="ml-8 flex flex-wrap gap-4 sm:gap-6 lg:gap-8">
                            {addOns.map((addOn) => (
                              <div key={addOn._id} className="relative">
                                <div className="absolute -left-2 top-0 bottom-0 w-1 bg-purple-400 rounded-l-lg"></div>
                                <CustomerSubscriptionCard
                                  subscription={addOn}
                                  isInactive={false}
                                  isMainSubscription={false}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // This is a standalone subscription (no add-ons)
                    return (
                      <CustomerSubscriptionCard
                        key={subscriptionOrGroup._id}
                        subscription={subscriptionOrGroup}
                        isInactive={false}
                        isMainSubscription={true}
                      />
                    );
                  }
                })
              ) : (
                <div className="col-span-full text-center text-gray-500 py-12">
                  <div className="text-xl font-medium mb-2">No active subscriptions found</div>
                  <div className="text-sm">Your active subscriptions will appear here</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upcoming Subscriptions Tab */}
        {activeTab === 1 && (
          <div className="bg-white rounded-b-xl p-4 sm:p-6">
            {inactiveSubscriptions.length > 0 ? (
              <div className="space-y-6">
                {aggregateUpcomingSubscriptions().map((aggregatedProduct) => (
                  <div key={aggregatedProduct.product._id} className="w-full">
                    {/* Aggregated Product Card */}
                    <div
                      className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-gray-300"
                      onClick={() => handleProductExpand(aggregatedProduct.product._id)}
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                        <div className="flex items-start gap-4 sm:gap-6">
                          <div className="bg-[#0F083E] text-white rounded-full p-3 flex-shrink-0">
                            {getProductIcon(aggregatedProduct.product.name)}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{aggregatedProduct.product.name}</h3>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                              {aggregatedProduct.product.description}
                            </p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                {aggregatedProduct.totalCount} subscription{aggregatedProduct.totalCount > 1 ? "s" : ""}
                              </span>
                              <span className="text-sm text-gray-500">
                                Available from:{" "}
                                <span className="font-medium">{formatDate(aggregatedProduct.earliestDate)}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
                          <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                            Scheduled
                          </span>
                          <svg
                            className={`w-6 h-6 text-gray-400 transition-transform duration-200 ${
                              expandedProduct === aggregatedProduct.product._id ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Individual Subscriptions */}
                    {expandedProduct === aggregatedProduct.product._id && (
                      <div className="mt-6 flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8">
                        {aggregatedProduct.subscriptions.map((subscription) => (
                          <CustomerSubscriptionCard
                            key={`inactive-${subscription._id}`}
                            subscription={subscription}
                            isInactive={true}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-16">
                <div className="text-xl font-medium mb-2">No upcoming subscriptions found</div>
                <div className="text-sm">Your scheduled subscriptions will appear here</div>
              </div>
            )}
          </div>
        )}
      </Box>

      {/* Contract Acceptance Modal */}
      <ContractAcceptanceModal
        isOpen={contractModalOpen && pendingContractsQueue.length > 0}
        onClose={handleContractModalClose}
        pendingContracts={pendingContractsQueue}
        onContractAccepted={handleContractAccepted}
      />
    </div>
  );
};

export default MySubscriptions;
